// react
import { useMemo, useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded';
import QuestionMarkRoundedIcon from '@mui/icons-material/QuestionMarkRounded';
import ReplayIcon from '@mui/icons-material/Replay';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

// material ui
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemText,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

// relative
import PublishedActuatorPlanChart from './PublishedActuatorPlanChart';

import { DateTime } from 'luxon';

// Function to get the appropriate icon based on status
const getStatusIcon = (status) => {
  switch (status) {
    case 'Error':
      return <ErrorRoundedIcon color="error" />;
    case 'Warning':
      return <WarningRoundedIcon color="warning" />;
    case 'Success':
      return <CheckCircleRoundedIcon color="success" />;
    case 'Critical':
      return <PriorityHighRoundedIcon color="error" />;
    case 'Unknown':
      return <QuestionMarkRoundedIcon color="gray" />;
    default:
      return null;
  }
};

const getModelStatusContent = (model) => {
  if (!model) return null;

  if (model.Status === 'Success') {
    return null;
  } else if (model.Status === 'Error') {
    return (
      <>
        {model.Errors && model.Errors.length > 0 && (
          <List>
            {model.Errors.map((error, index) => (
              <ListItem gutterBottom key={index}>
                <ListItemText
                  primary={error.error_type}
                  secondary={error.message}
                  slotProps={{
                    primary: {
                      variant: 'body1',
                      color: 'text.secondary',
                      fontSize: '0.8rem',
                    },

                    secondary: {
                      variant: 'body1',
                      color: 'text.primary',
                    }
                  }}>
                  {typeof error.message === 'string'
                    ? error.error_type + ': ' + error.message
                    : JSON.stringify(error.message)}
                </ListItemText>
              </ListItem>
            ))}
          </List>
        )}
      </>
    );
  } else {
    return (
      <>
        {model.Warnings && model.Warnings.length > 0 && (
          <ul>
            {model.Warnings.map((warning, index) => (
              <li key={index}>
                <Typography variant="body2">
                  {typeof warning.message === 'string'
                    ? warning.message
                    : JSON.stringify(warning.message)}
                </Typography>
              </li>
            ))}
          </ul>
        )}
      </>
    );
  }
};

const formatActivationIntervals = (intervals) => {
  return intervals
    ?.filter((interval) => interval.setpoint === 100) // Only include intervals where setpoint === 100
    ?.map((interval, index) => {
      const start = DateTime.fromISO(interval.interval_start);
      const end = DateTime.fromISO(interval.interval_end);

      const startDay = start.toFormat('cccc'); // Start day, e.g., "Monday"
      const startDate = start.toFormat('MMM dd, yyyy'); // Start date, e.g., "Nov 20, 2024"
      const startTime = start.toFormat('HH:mm'); // Start time, e.g., "08:00"

      const endDay = end.toFormat('cccc'); // End day, e.g., "Monday"
      const endDate = end.toFormat('MMM dd, yyyy'); // End date, e.g., "Nov 20, 2024"
      const endTime = end.toFormat('HH:mm'); // End time, e.g., "12:00"

      // Include day, date, and time for both start and end
      const formattedInterval = `${startDay}, ${startDate}, ${startTime} - ${endDay}, ${endDate}, ${endTime}`;

      return (
        <div
          key={index}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '8px',
          }}
        >
          <span>{formattedInterval}</span>
        </div>
      );
    });
};

const formatDaysSince = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();

  if (isNaN(date.getTime())) {
    return '-';
  }

  // Calculate the difference in time (milliseconds)
  const diffTime = Math.abs(now - date);

  // Convert time difference to days
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return `${diffDays} day(s) ago`;
};

const OccupancyPredictionForm = (props) => {
  const {
    open,
    onClose,
    subscription,
    initialPrediction,
    actuatorPlan,
    generateOccupancyPredictionAnalysis,
    generatedOccupancyPlan,
    isGenerating,
    occupancySourceExclusions,
    setGeneratedOccupancyPlan,
  } = props;
  const { t } = useTranslation();
  const [duration, setDuration] = useState('');
  const [filterErraticPredictions, setFilterErraticPredictions] =
    useState(true);
  // const [formattedIntervals, setFormattedIntervals] = useState([]);

  const activationIntervals = formatActivationIntervals(
    actuatorPlan?.actuator_plan,
  );

  // Helper function to initialize exclusions based on occupancySourceExclusions
  const initializeExclusions = (sourceExclusions) => {
    const defaultState = {
      baseline: false,
      flow: false,
      power: false,
      temperature: false,
    };

    // Update state based on the presence of strings in occupancySourceExclusions
    const updatedState = Object.keys(defaultState)?.reduce((state, key) => {
      state[key] = sourceExclusions?.includes(key);
      return state;
    }, {});

    return updatedState;
  };

  // Initialize the state dynamically
  const [exclusions, setExclusions] = useState(
    initializeExclusions(occupancySourceExclusions),
  );

  const handleReset = () => {
    setExclusions(initializeExclusions(occupancySourceExclusions));
    setFilterErraticPredictions(true);
    setGeneratedOccupancyPlan(null);
  };

  // Extracting the model outside of the JSX
  const model = subscription?.State?.MachineLearning?.MachineLearningInfo?.find(
    (model) => model.Name === 'PredictiveOccupancyDetection',
  );

  const formatOccupancyIntervals = (intervals) => {
    return intervals
      ?.filter((interval) => interval.label) // Only include intervals where label is true
      ?.map((interval, index) => {
        const start = DateTime.fromISO(interval.interval_start);
        const end = DateTime.fromISO(interval.interval_end);

        const startDay = start.toFormat('cccc'); // Start day, e.g., "Monday"
        const startDate = start.toFormat('MMM dd, yyyy'); // Start date, e.g., "Nov 20, 2024"
        const startTime = start.toFormat('HH:mm'); // Start time, e.g., "08:00"

        const endDay = end.toFormat('cccc'); // End day, e.g., "Monday"
        const endDate = end.toFormat('MMM dd, yyyy'); // End date, e.g., "Nov 20, 2024"
        const endTime = end.toFormat('HH:mm'); // End time, e.g., "12:00"

        // Include day, date, and time for both start and end
        const formattedInterval = `${startDay}, ${startDate}, ${startTime} - ${endDay}, ${endDate}, ${endTime}`;

        return (
          <div
            key={index}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '8px',
            }}
          >
            <span>{formattedInterval}</span>
          </div>
        );
      });
  };

  // useEffect(() => {
  //   if (generatedOccupancyPlan) {
  //     setFormattedIntervals(formatOccupancyIntervals(generatedOccupancyPlan));
  //   } else if (!generatedOccupancyPlan && initialPrediction) {
  //     setFormattedIntervals(
  //       formatOccupancyIntervals(initialPrediction.occupancy_plan),
  //     );
  //   }
  // }, [generatedOccupancyPlan, initialPrediction]);

  const formattedIntervals = useMemo(() => {
    const plan =
      generatedOccupancyPlan ?? initialPrediction?.occupancy_plan ?? [];

    return formatOccupancyIntervals(plan) ?? [];
  }, [generatedOccupancyPlan, initialPrediction]);

  const handleExclusionChange = (name) => {
    setExclusions({
      ...exclusions,
      [name]: !exclusions[name],
    });
  };

  const formattedExclusions = Object.keys(exclusions).filter(
    (key) => exclusions[key],
  );

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission behavior
    const formData = {
      duration,
      filterErraticPredictions,
      exclusions: formattedExclusions,
    };
    await generateOccupancyPredictionAnalysis(formData); // Pass the form data to the parent
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {subscription?.JobSubscriptionId} - {subscription?.DisplayName}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Tooltip
              title={t(
                'buildingOptimization:tooltips.generateOccupancySchedule',
              )}
              placement="top"
            >
              <>
                <Button
                  variant="standard"
                  onClick={handleReset}
                  sx={{
                    textTransform: 'none',
                    ml: 1,
                  }}
                >
                  {t('actions.reset')}
                </Button>
                <Button
                  onClick={handleSubmit}
                  endIcon={
                    generatedOccupancyPlan && !isGenerating ? (
                      <ReplayIcon />
                    ) : null
                  }
                  variant="contained"
                  disableElevation
                  loading={props.loading}
                  {...props}
                >
                  {isGenerating ? (
                    <>
                      {t('occupancyPredictionForm.generating', {
                        ns: 'buildingOptimization',
                      })}{' '}
                      <CircularProgress
                        size={20}
                        color="inherit"
                        sx={{ ml: 2 }}
                      />
                    </>
                  ) : generatedOccupancyPlan ? (
                    'Regenerate'
                  ) : (
                    'Generate'
                  )}
                </Button>
              </>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid size={4}>
            <Card sx={{ padding: 2 }}>
              <ListItemText
                primary="Predictive Occupancy Detection Model Type"
                secondary={subscription ? `${model?.ModelType}` : ''}
                slotProps={{
                  primary: {
                    variant: 'label',
                    fontSize: '0.8rem',
                    color: 'text.secondary',
                  },

                  secondary: {
                    variant: 'body1',
                    color: 'text.primary',
                    mt: 1,
                  }
                }} />
            </Card>
          </Grid>
          <Grid size={4}>
            <Card sx={{ padding: 2 }}>
              <ListItemText
                primary="Predictive Occupancy Detection Model Refresh Time"
                // Use the helper function here
                secondary={
                  <Box sx={{ display: 'flex', mt: 1 }}>
                    {model?.Status && (
                      <Tooltip
                        title={getModelStatusContent(model)}
                        placement="top"
                      >
                        {getStatusIcon(model?.Status)}
                      </Tooltip>
                    )}
                    <Typography variant="subtitle" sx={{ ml: 1 }}>
                      {t('occupancyPredictionForm.refreshed', {
                        ns: 'buildingOptimization',
                      })}{' '}
                      {formatDaysSince(model?.ModelRefreshTime)}
                    </Typography>
                  </Box>
                }
                slotProps={{
                  primary: {
                    variant: 'label',
                    fontSize: '0.8rem',
                    color: 'text.secondary',
                  },

                  secondary: {
                    variant: 'body1',
                    color: 'text.primary',
                  }
                }} />
            </Card>
          </Grid>
          <Grid size={4}>
            <Card sx={{ padding: 2 }}>
              <ListItemText
                primary="Predictive Occupancy Detection Model Update Time"
                // Use the helper function here
                secondary={
                  <Box sx={{ display: 'flex', mt: 1 }}>
                    {model?.Status && (
                      <Tooltip
                        title={getModelStatusContent(model)}
                        placement="top"
                      >
                        {getStatusIcon(model?.Status)}
                      </Tooltip>
                    )}
                    <Typography variant="subtitle" sx={{ ml: 1 }}>
                      {t('occupancyPredictionForm.updated', {
                        ns: 'buildingOptimization',
                      })}{' '}
                      {formatDaysSince(model?.ModelRefreshTime)}
                    </Typography>
                  </Box>
                }
                slotProps={{
                  primary: {
                    variant: 'label',
                    fontSize: '0.8rem',
                    color: 'text.secondary',
                  },

                  secondary: {
                    variant: 'body1',
                    color: 'text.primary',
                  }
                }} />
            </Card>
          </Grid>

          <Grid size={12}>
            <Card sx={{ padding: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  position: 'relative',
                }}
              >
                <Typography gutterBottom sx={{ color: 'text.secondary' }}>
                  {t('occupancyPredictionForm.analysisInput', {
                    ns: 'buildingOptimization',
                  })}
                </Typography>
                <Tooltip
                  arrow
                  title={t(
                    'buildingOptimization:tooltips.minDurationOccupancyEvent',
                  )}
                  placement="top"
                >
                  <InfoRoundedIcon sx={{ ml: 1, color: 'lightgray' }} />
                </Tooltip>
              </Box>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TextField
                      type="number"
                      label="Minimum Duration"
                      value={duration}
                      onChange={(e) => setDuration(e.target.value)}
                      fullWidth
                      sx={{ mt: 1 }}
                      optional
                      slotProps={{
                        htmlInput: { min: 0, step: 1 }
                      }}
                    />
                  </Box>
                </Grid>
                <Grid size={12}>
                  {['baseline', 'flow', 'power', 'temperature', 'co2'].map(
                    (option) => (
                      <Tooltip
                        key={option}
                        arrow
                        title={`If checked, ${option} will be excluded from the analysis.`}
                        placement="top"
                      >
                        <FormControlLabel
                          key={option}
                          sx={{ ml: 1 }}
                          control={
                            <Switch
                              checked={exclusions[option]}
                              onChange={() => handleExclusionChange(option)}
                              size="small"
                            />
                          }
                          label={option}
                        />
                      </Tooltip>
                    ),
                  )}
                  <Tooltip
                    arrow
                    title={t(
                      'buildingOptimization:tooltips.filterErraticPredictions',
                    )}
                    placement="top"
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={filterErraticPredictions}
                          onChange={(e) =>
                            setFilterErraticPredictions(e.target.checked)
                          }
                          size="small"
                          sx={{
                            ml: 2,
                          }}
                        />
                      }
                      label="Filter Erratic Predictions"
                    />
                  </Tooltip>
                </Grid>
              </Grid>
            </Card>
          </Grid>

          <Grid size={6}>
            <Card sx={{ padding: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  position: 'relative',
                  mb: 4,
                }}
              >
                <Typography gutterBottom sx={{ color: 'text.secondary' }}>
                  {t('occupancyPredictionForm.occupiedPlan', {
                    ns: 'buildingOptimization',
                  })}
                </Typography>
                <Tooltip
                  arrow
                  title={t(
                    'buildingOptimization:tooltips.occupancyPlanDisplay',
                  )}
                  placement="top"
                >
                  <InfoRoundedIcon sx={{ ml: 1, color: 'lightgray' }} />
                </Tooltip>
              </Box>
              {formattedIntervals?.length > 0 ? (
                formattedIntervals?.map((interval, index) => (
                  <Typography key={index} variant="body1">
                    {interval}
                  </Typography>
                ))
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <Typography sx={{ fontSize: 14 }}>
                    {t('occupancyPredictionForm.generateToView', {
                      ns: 'buildingOptimization',
                    })}
                  </Typography>
                </Box>
              )}
            </Card>
          </Grid>
          <Grid size={6}>
            <Card sx={{ padding: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  position: 'relative',
                  mb: 4,
                }}
              >
                <Typography gutterBottom sx={{ color: 'text.secondary' }}>
                  {t('occupancyPredictionForm.scheduledActivations', {
                    ns: 'buildingOptimization',
                  })}
                </Typography>
                <Tooltip
                  arrow
                  title={t(
                    'buildingOptimization:tooltips.scheduledActivations',
                  )}
                  placement="top"
                >
                  <InfoRoundedIcon sx={{ ml: 1, color: 'lightgray' }} />
                </Tooltip>
              </Box>
              {activationIntervals?.length > 0 ? (
                activationIntervals?.map((interval, index) => (
                  <Typography key={index} variant="body1">
                    {interval}
                  </Typography>
                ))
              ) : (
                <Typography variant="body1">
                  {t('occupancyPredictionForm.noScheduledActivations', {
                    ns: 'buildingOptimization',
                  })}
                </Typography>
              )}
            </Card>
          </Grid>

          <Grid size={12}>
            <Card sx={{ padding: 2 }}>
              <PublishedActuatorPlanChart
                actuatorPlan={actuatorPlan}
                occupancyPrediction={initialPrediction}
                generatedOccupancyPrediction={generatedOccupancyPlan}
              />
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default OccupancyPredictionForm;

